/**
 * The six grouped code boxes (front-door.md §3.3 "code sent"), in the
 * Switchboard's brass chrome. Copied from the Agency UI verbatim — it depends
 * only on react + The Study tokens (the brass focus ring is already house metal,
 * so it needs no agent accent). Auto-advance, backspace-steps-back, paste-fills-
 * all, auto-submit on the sixth digit.
 */
import { useEffect, useRef } from "react";

export function CodeBoxes(props: {
  value: string;
  onChange: (next: string) => void;
  onComplete: (code: string) => void;
  shake?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const { value, onChange, onComplete, shake, disabled, invalid, autoFocus } = props;
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (value.length === 6 && /^\d{6}$/.test(value)) onComplete(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const setAt = (i: number, ch: string) => {
    const arr = value.padEnd(6, " ").slice(0, 6).split("");
    arr[i] = ch;
    onChange(arr.join("").replace(/ /g, "").slice(0, 6));
  };

  const handleInput = (i: number, raw: string) => {
    const ch = raw.replace(/\D/g, "").slice(-1);
    if (!ch) return;
    setAt(i, ch);
    if (i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[i] && digits[i] !== " ") {
        setAt(i, " ");
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        setAt(i - 1, " ");
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const boxClass = [
    "h-14 w-11 rounded-input border bg-surface text-center font-mono text-[20px] text-ink",
    "outline-none transition-colors",
    "focus:border-brass focus:ring-2 focus:ring-brass/35",
    invalid ? "border-problem" : "border-hairline",
  ].join(" ");

  return (
    <div
      className="flex justify-center gap-2"
      data-shake={shake ? "1" : undefined}
      role="group"
      aria-label="Enter your 6-digit code"
    >
      {Array.from({ length: 6 }).map((_, i) => {
        const ch = digits[i] === " " ? "" : digits[i];
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={ch}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            disabled={disabled}
            aria-label={`Digit ${i + 1}`}
            onChange={(e) => handleInput(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            className={`${boxClass} ${i === 3 ? "ml-2" : ""}`}
          />
        );
      })}
    </div>
  );
}
